// src/pages/frontend/AuthTabs.tsx
import { Email, Phone } from "@mui/icons-material";
import {
  Alert,
  Box,
  Divider,
  Paper,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { CredentialResponse } from "@react-oauth/google";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";

import GoogleAuth from "../../components/frontend/auth/GoogleAuth";
import GoogleRegistrationForm from "../../components/frontend/auth/GoogleRegistrationForm";
import LoginForm from "../../components/frontend/auth/LoginForm";
import ManualRegisterForm from "../../components/frontend/auth/ManualRegisterForm";
import PhoneLoginForm from "../../components/frontend/auth/PhoneLoginForm";
import PhoneRegisterForm from "../../components/frontend/auth/PhoneRegisterForm";
import { useAuthActions } from "../../hooks/useAuthActions";
import { useAuthNavigation } from "../../hooks/useAuthNavigation";
import { sendLoginPhoneOtp, sendPhoneOtp, verifyPhoneOtp } from "../../services/authService";
import { RegisterFormData } from "../../validation/registerSchema";

const AuthTabs: React.FC = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t: myTranslate } = useTranslation();
  const [activeTab, setActiveTab] = useState<number>(() =>
    searchParams.get("tab") === "register" ? 1 : 0
  ); // 0=Login, 1=Register

  const [authMode, setAuthMode] = useState<"email" | "phone">(() =>
    searchParams.get("mode") === "email" ? "email" : "phone"
  );

  const {
    login,
    register,
    googleAuth,
    googleRegister,
    phoneLogin: phoneLoginMutation,
    phoneRegister: phoneRegisterMutation,
    phoneOtpLogin: phoneOtpLoginMutation,
    isLoading,
    errorMessage,
  } = useAuthActions();
  const { navigateToDestination, navigateToForgotPassword } =
    useAuthNavigation();

  // Sync tab and mode from URL (only when the URL changes, avoids feedback loops)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    
    const tabParam = params.get("tab");
    const tabFromUrl = tabParam === "register" ? 1 : 0;
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }

    const modeParam = params.get("mode");
    const modeFromUrl = modeParam === "email" ? "email" : "phone";
    if (modeFromUrl !== authMode) {
      setAuthMode(modeFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Keep URL in sync when tab or mode changes, without re-triggering the loop
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let changed = false;

    const desiredTab = activeTab === 1 ? "register" : "login";
    if (params.get("tab") !== desiredTab) {
      params.set("tab", desiredTab);
      changed = true;
    }

    if (params.get("mode") !== authMode) {
      params.set("mode", authMode);
      changed = true;
    }

    if (changed) {
      setSearchParams(params, { replace: true });
    }
  }, [activeTab, authMode, location.search, setSearchParams]);

  // Flows
  useEffect(() => {
    if (login.isSuccess) navigateToDestination();
  }, [login.isSuccess, navigateToDestination]);

  useEffect(() => {
    if (googleRegister.isSuccess) navigateToDestination();
  }, [googleRegister.isSuccess, navigateToDestination]);

  useEffect(() => {
    if (phoneLoginMutation.isSuccess) navigateToDestination();
  }, [phoneLoginMutation.isSuccess, navigateToDestination]);

  useEffect(() => {
    if (phoneRegisterMutation.isSuccess) navigateToDestination();
  }, [phoneRegisterMutation.isSuccess, navigateToDestination]);

  useEffect(() => {
    if (phoneOtpLoginMutation.isSuccess) navigateToDestination();
  }, [phoneOtpLoginMutation.isSuccess, navigateToDestination]);

  const [showGoogleRegistration, setShowGoogleRegistration] = useState(false);
  const [googleData, setGoogleData] = useState<{
    idToken: string;
    googleUser: {
      email: string;
      firstName: string;
      lastName: string;
      profilePicture?: string;
    };
  } | null>(null);

  useEffect(() => {
    if (googleAuth.isSuccess && googleAuth.data?.data) {
      const data = googleAuth.data.data;
      if (data.isNewUser) {
        try {
          const decoded = JSON.parse(
            atob((data.token || "").split(".")[1] || "")
          );
          setShowGoogleRegistration(true);
          setGoogleData((prev) => ({
            idToken: data.token || prev?.idToken || "",
            googleUser: {
              email: decoded?.email || "",
              firstName: decoded?.given_name || "",
              lastName: decoded?.family_name || "",
              profilePicture: decoded?.picture,
            },
          }));
        } catch {
          setShowGoogleRegistration(true);
        }
      } else {
        navigateToDestination();
      }
    }
  }, [
    googleAuth.isSuccess,
    googleAuth.data,
    googleAuth.isError,
    googleAuth.error,
    googleAuth.isPending,
    navigateToDestination,
  ]);

  const resetAllMutations = () => {
    try {
      login.reset();
      googleAuth.reset();
      register.reset();
      googleRegister.reset();
      phoneLoginMutation.reset();
      phoneRegisterMutation.reset();
      phoneOtpLoginMutation.reset();
    } catch {}
  };

  const handleTabChange = (_: React.SyntheticEvent, v: number) => {
    resetAllMutations();
    setActiveTab(v);
  };

  // Submit handlers
  const handleLoginSubmit = (data: { email: string; password: string }) =>
    login.mutate(data);

  const handleManualRegister = (data: RegisterFormData) =>
    register.mutateAsync(data);

  // Phone auth handlers
  const handlePhoneLogin = async (data: { phoneNumber: string; password: string }) => {
    try {
      await phoneLoginMutation.mutateAsync(data);
    } catch {
      // Error handled by mutation state
    }
  };

  const handlePhoneOtpLogin = async (data: { phoneNumber: string; otp: string }) => {
    try {
      await phoneOtpLoginMutation.mutateAsync(data);
    } catch {
      // Error handled by mutation state
    }
  };

  const handlePhoneSendOtp = async (phone: string, countryCode: string) => {
    try {
      const result = await sendPhoneOtp({ phoneNumber: phone, countryCode });
      if (!result.isSuccess) {
        throw new Error(result.message || "SMS_SEND_FAILED");
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const msg = axiosErr?.response?.data?.message
        || (err instanceof Error ? err.message : "SMS_SEND_FAILED");
      throw new Error(msg);
    }
  };

  const handleLoginPhoneSendOtp = async (phone: string, countryCode: string) => {
    try {
      const result = await sendLoginPhoneOtp({ phoneNumber: phone, countryCode });
      if (!result.isSuccess) {
        throw new Error(result.message || "SMS_SEND_FAILED");
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const msg = axiosErr?.response?.data?.message
        || (err instanceof Error ? err.message : "SMS_SEND_FAILED");
      throw new Error(msg);
    }
  };

  const handlePhoneVerifyOtp = async (phone: string, countryCode: string, otp: string) => {
    const result = await verifyPhoneOtp({ phoneNumber: phone, countryCode, otp });
    if (!result.isSuccess) {
      throw new Error(result.message || "OTP_INVALID");
    }
  };

  const handlePhoneRegister = async (data: Record<string, unknown>) => {
    try {
      await phoneRegisterMutation.mutateAsync({
        phoneNumber: data.phoneNumber as string,
        countryCode: data.countryCode as string,
        email: data.email as string,
        firstName: data.firstName as string,
        lastName: data.lastName as string,
        password: data.password as string,
        confirmPassword: data.confirmPassword as string,
        referralCode: data.referralCode as string | undefined,
      });
    } catch {
      // Error is handled by mutation state (errorMessage) — don't rethrow
    }
  };

  const handleGoogleSuccess = (payload: {
    data: CredentialResponse;
    type: "login" | "register";
  }) => {
    if (payload.type === "login" || payload.type === "register") {
      try {
        const credential = payload.data.credential ?? "";
        const dec = credential
          ? JSON.parse(atob(credential.split(".")[1]))
          : {};

        interface GoogleTokenPayload {
          email?: string;
          given_name?: string;
          family_name?: string;
          picture?: string;
        }
        const tokenPayload = dec as GoogleTokenPayload;

        setGoogleData({
          idToken: credential,
          googleUser: {
            email: tokenPayload.email || "",
            firstName: tokenPayload.given_name || "",
            lastName: tokenPayload.family_name || "",
            profilePicture: tokenPayload.picture,
          },
        });

        googleAuth.mutate({ idToken: payload.data.credential ?? "" });
      } catch (error) {
        // Error parsing token - handled silently
      }
    }
  };

  const handleGoogleRegistration = (data: Record<string, unknown>) => {
    if (!googleData) return;
    googleRegister.mutate({
      idToken: googleData.idToken,
      email: (data.email as string) || "",
      firstName: (data.firstName as string) || "",
      lastName: (data.lastName as string) || "",
      phoneNumber: (data.phoneNumber as string) || "",
    });
  };

  return (
    <Box
      className="notranslate"
      translate="no"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        px: 2,
        py: 4,
        minHeight: "auto",
      }}
    >
      <Paper
        elevation={0}
        className="notranslate"
        translate="no"
        sx={{
          width: "100%",
          maxWidth: 500,
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          border: "1px solid",
          borderColor: "divider",
          borderTop: "3px solid",
          borderTopColor: "primary.main",
        }}
      >
        {showGoogleRegistration && googleData ? (
          <>
            <Typography
              variant="h4"
              align="center"
              className="notranslate"
              translate="no"
              gutterBottom
              sx={{ mb: 3 }}
            >
              {myTranslate("auth.completeAccount")}
            </Typography>
            <GoogleRegistrationForm
              googleUser={googleData.googleUser}
              onSubmit={handleGoogleRegistration}
              isLoading={isLoading}
              error={errorMessage}
            />
          </>
        ) : (
          <>
            <Typography
              variant="h4"
              align="center"
              className="notranslate"
              translate="no"
              gutterBottom
              sx={{ mb: 3 }}
            >
              {activeTab === 0
                ? myTranslate("auth.welcomeBack")
                : myTranslate("auth.createAccount")}
            </Typography>

            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                mb: 2,
                "& .MuiTab-root": {
                  color: "text.secondary",
                  borderBottom: "2px solid",
                  borderBottomColor: "grey.300",
                  "&.Mui-selected": {
                    color: "primary.main",
                    borderBottomColor: "primary.main",
                  },
                },
                "& .MuiTabs-indicator": {
                  display: "none", // Hide the default indicator
                },
              }}
              className="notranslate"
              translate="no"
            >
              <Tab label={myTranslate("auth.login")} />
              <Tab label={myTranslate("auth.register")} />
            </Tabs>

            {activeTab === 0 && authMode === "email" && errorMessage && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {errorMessage}
              </Alert>
            )}

            {/* Email / Phone Toggle */}
            <ToggleButtonGroup
              value={authMode}
              exclusive
              onChange={(_e, v) => { if (v) { resetAllMutations(); setAuthMode(v); } }}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
            >
              <ToggleButton value="phone">
                <Phone sx={{ mr: 1, fontSize: 18 }} />
                {myTranslate("auth.usePhone")}
              </ToggleButton>
              <ToggleButton value="email">
                <Email sx={{ mr: 1, fontSize: 18 }} />
                {myTranslate("auth.useEmail")}
              </ToggleButton>
            </ToggleButtonGroup>

            {activeTab === 0 ? (
              <>
                {authMode === "email" && (
                  <>
                    <GoogleAuth
                      type="login"
                      onSuccess={handleGoogleSuccess}
                      onError={() => { /* Google auth error handled silently */ }}
                      isLoading={googleAuth.isPending}
                    />
                    <Divider sx={{ my: 3 }}>
                      <Box component="span" className="notranslate" translate="no" sx={{ color: "text.secondary" }}>
                        {myTranslate("auth.or")}
                      </Box>
                    </Divider>
                    <LoginForm
                      onSubmit={handleLoginSubmit}
                      isLoading={isLoading}
                      onForgotPassword={() => navigateToForgotPassword("email")}
                      onRegister={() => setActiveTab(1)}
                    />
                  </>
                )}
                {authMode === "phone" && (
                  <PhoneLoginForm
                    onLogin={handlePhoneLogin}
                    onOtpLogin={handlePhoneOtpLogin}
                    onSendOtp={handleLoginPhoneSendOtp}
                    onForgotPassword={() => navigateToForgotPassword("phone")}
                    isLoading={isLoading}
                    error={errorMessage}
                  />
                )}
              </>
            ) : (
              <>
                {authMode === "email" && (
                  <>
                    <GoogleAuth
                      type="register"
                      onSuccess={handleGoogleSuccess}
                      onError={() => { /* Google auth error handled silently */ }}
                      isLoading={googleAuth.isPending}
                    />
                    <Divider sx={{ my: 3 }}>
                      <Box component="span" className="notranslate" translate="no" sx={{ color: "text.secondary" }}>
                        {myTranslate("auth.or")}
                      </Box>
                    </Divider>
                    <ManualRegisterForm
                      onSubmit={handleManualRegister}
                      isLoading={isLoading}
                      error={errorMessage}
                      onLogin={() => setActiveTab(0)}
                    />
                  </>
                )}
                {authMode === "phone" && (
                  <PhoneRegisterForm
                    onSubmit={handlePhoneRegister}
                    onSendOtp={handlePhoneSendOtp}
                    onVerifyOtp={handlePhoneVerifyOtp}
                    isLoading={isLoading}
                    error={errorMessage}
                  />
                )}
              </>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default AuthTabs;
